import React, { useState, useMemo } from 'react';
import {
    Upload,
    FileText,
    Table,
    RefreshCw,
    Download,
    Clock,
    CheckCircle,
    AlertTriangle,
    ArrowRight,
    FileSpreadsheet,
    History,
    Info,
    Loader2
} from 'lucide-react';
import { useTranslation } from '../context/LanguageContext';
import { useActivityLog, LOG_TYPES, LOG_CATEGORIES } from '../context/ActivityLogContext';
import ImportModal from '../components/Import/ImportModal';
import { generateTemplate } from '../utils/importService';
import { useImportHistory, useImportHistoryMutations } from '../hooks';

function Import() {
    const { t } = useTranslation();
    const { addLog } = useActivityLog();
    const [showImportModal, setShowImportModal] = useState(false);
    const [selectedType, setSelectedType] = useState(null);


    const { imports: dbHistory = [], loading: historyLoading, refetch: refetchHistory } = useImportHistory();
    const { createImport: createImportRecord } = useImportHistoryMutations();


    const recentImports = useMemo(() => {
        return dbHistory.map(item => ({
            id: item.id,
            type: item.type === 'financial_update' ? 'financial' : item.type,
            fileName: item.file_name || 'Unknown file',
            date: item.created_at,
            recordsImported: item.records_imported || 0,
            recordsSkipped: item.records_skipped || 0,
            status: item.status === 'completed' ? 'success' : item.status === 'failed' ? 'error' : 'warning'
        }));
    }, [dbHistory]);

    const IMPORT_TYPES = {
        loans: {
            label: t('importPage.loans'),
            description: t('importPage.loansDescription'),
            icon: FileText,
            color: 'blue',
            bgColor: 'bg-blue-100',
            textColor: 'text-blue-600',
            borderColor: 'border-blue-200',
            hoverBorder: 'hover:border-blue-500'
        },
        covenants: {
            label: t('importPage.covenants'),
            description: t('importPage.covenantsDescription'),
            icon: Table,
            color: 'purple',
            bgColor: 'bg-purple-100',
            textColor: 'text-purple-600',
            borderColor: 'border-purple-200',
            hoverBorder: 'hover:border-purple-500'
        },
        financial: {
            label: t('importPage.financialUpdate'),
            description: t('importPage.financialUpdateDescription'),
            icon: RefreshCw,
            color: 'emerald',
            bgColor: 'bg-emerald-100',
            textColor: 'text-emerald-600',
            borderColor: 'border-emerald-200',
            hoverBorder: 'hover:border-emerald-500'
        }
    };

    const handleStartImport = (type) => {
        setSelectedType(type);
        setShowImportModal(true);
    };

    const handleImportComplete = async (result) => {

        const importData = {
            type: result.type === 'financial' ? 'financial_update' : result.type,
            fileName: result.fileName || 'Imported file',
            recordsImported: result.count || 0,
            recordsSkipped: result.skipped || 0,
            status: result.errors && result.errors.length > 0 ? 'completed' : 'completed'
        };

        await createImportRecord(importData, null);


        addLog({
            type: LOG_TYPES.ACTION,
            category: LOG_CATEGORIES.IMPORT,
            action: 'import_data',
            message: `Import ${result.type}: ${result.fileName || 'fichier'} - ${result.count || 0} enregistrements`,
            details: {
                importType: result.type,
                fileName: result.fileName,
                recordsImported: result.count || 0,
                recordsSkipped: result.skipped || 0,
                hasErrors: result.errors && result.errors.length > 0
            }
        });

        refetchHistory();
    };

    const formatDate = (dateStr) => {
        const date = new Date(dateStr);
        return date.toLocaleDateString('en-GB', {
            day: 'numeric',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    return (
        <div className="space-y-6">
            {}
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-bold text-slate-800">{t('importPage.title')}</h1>
                    <p className="text-slate-500 mt-1">
                        {t('importPage.subtitle')}
                    </p>
                </div>
            </div>

            {}
            <div className="grid grid-cols-3 gap-6">
                {Object.entries(IMPORT_TYPES).map(([key, config]) => {
                    const Icon = config.icon;
                    return (
                        <div
                            key={key}
                            className={`bg-white rounded-xl border-2 ${config.borderColor} ${config.hoverBorder} p-6 cursor-pointer transition-all hover:shadow-lg group`}
                            onClick={() => handleStartImport(key)}
                        >
                            <div className="flex items-start justify-between mb-4">
                                <div className={`p-3 rounded-xl ${config.bgColor} group-hover:scale-110 transition-transform`}>
                                    <Icon size={24} className={config.textColor} />
                                </div>
                                <ArrowRight size={20} className="text-slate-300 group-hover:text-slate-500 group-hover:translate-x-1 transition-all" />
                            </div>
                            <h3 className="text-lg font-semibold text-slate-800 mb-2">
                                {t('importPage.import')} {config.label}
                            </h3>
                            <p className="text-sm text-slate-500 mb-4">
                                {config.description}
                            </p>
                            <div className="flex items-center gap-2">
                                <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        generateTemplate(key, { format: 'csv' });
                                    }}
                                    className="text-xs text-blue-600 hover:underline flex items-center gap-1"
                                >
                                    <Download size={12} />
                                    CSV
                                </button>
                                <span className="text-slate-300">|</span>
                                <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        generateTemplate(key, { format: 'xlsx' });
                                    }}
                                    className="text-xs text-emerald-600 hover:underline flex items-center gap-1"
                                >
                                    <Download size={12} />
                                    Excel
                                </button>
                            </div>
                        </div>
                    );
                })}
            </div>

            {}
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-6 border border-blue-100">
                <div className="flex items-start gap-4">
                    <div className="p-2 bg-blue-100 rounded-lg">
                        <Info size={20} className="text-blue-600" />
                    </div>
                    <div className="flex-1">
                        <h3 className="font-semibold text-slate-800 mb-2">{t('importPage.quickStartGuide')}</h3>
                        <div className="grid grid-cols-3 gap-6 text-sm">
                            <div>
                                <h4 className="font-medium text-slate-700 mb-1">{t('importPage.supportedFormatsTitle')}</h4>
                                <ul className="text-slate-600 space-y-1">
                                    <li className="flex items-center gap-2">
                                        <FileSpreadsheet size={14} className="text-emerald-600" />
                                        {t('importPage.excelFormat')}
                                    </li>
                                    <li className="flex items-center gap-2">
                                        <FileText size={14} className="text-blue-600" />
                                        {t('importPage.csvFormat')}
                                    </li>
                                </ul>
                            </div>
                            <div>
                                <h4 className="font-medium text-slate-700 mb-1">{t('importPage.numberFormatsTitle')}</h4>
                                <ul className="text-slate-600 space-y-1">
                                    <li>{t('importPage.europeanNumberFormat')}</li>
                                    <li>{t('importPage.usNumberFormat')}</li>
                                    <li>{t('importPage.multiplierFormat')}</li>
                                </ul>
                            </div>
                            <div>
                                <h4 className="font-medium text-slate-700 mb-1">{t('importPage.dateFormatsTitle')}</h4>
                                <ul className="text-slate-600 space-y-1">
                                    <li>{t('importPage.europeanDateFormat')}</li>
                                    <li>{t('importPage.isoDateFormat')}</li>
                                    <li>{t('importPage.textDateFormat')}</li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {}
            <div className="bg-white rounded-xl border border-slate-200">
                <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <History size={20} className="text-slate-500" />
                        <h2 className="text-lg font-semibold text-slate-800">{t('importPage.recentImports')}</h2>
                    </div>
                    <span className="text-sm text-slate-500">{recentImports.length} {t('importPage.importsCount')}</span>
                </div>

                {historyLoading ? (
                    <div className="px-6 py-12 text-center">
                        <Loader2 className="h-8 w-8 animate-spin text-blue-600 mx-auto mb-3" />
                        <p className="text-slate-500">Loading import history...</p>
                    </div>
                ) : recentImports.length > 0 ? (
                    <div className="divide-y divide-slate-100">
                        {recentImports.map((item) => {
                            const config = IMPORT_TYPES[item.type];
                            const Icon = config?.icon || FileText;

                            return (
                                <div key={item.id} className="px-6 py-4 flex items-center justify-between hover:bg-slate-50">
                                    <div className="flex items-center gap-4">
                                        <div className={`p-2 rounded-lg ${config?.bgColor || 'bg-slate-100'}`}>
                                            <Icon size={18} className={config?.textColor || 'text-slate-600'} />
                                        </div>
                                        <div>
                                            <p className="font-medium text-slate-800">{item.fileName}</p>
                                            <div className="flex items-center gap-3 text-sm text-slate-500">
                                                <span>{config?.label || item.type}</span>
                                                <span>•</span>
                                                <span className="flex items-center gap-1">
                                                    <Clock size={12} />
                                                    {formatDate(item.date)}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-6">
                                        <div className="text-right">
                                            <p className="text-sm font-medium text-emerald-600">
                                                {item.recordsImported} {item.recordsImported > 1 ? t('importPage.importedPlural') : t('importPage.imported')}
                                            </p>
                                            {item.recordsSkipped > 0 && (
                                                <p className="text-xs text-amber-600">
                                                    {item.recordsSkipped} {item.recordsSkipped > 1 ? t('importPage.skippedPlural') : t('importPage.skipped')}
                                                </p>
                                            )}
                                        </div>
                                        <div className={`p-1.5 rounded-full ${
                                            item.status === 'success'
                                                ? 'bg-emerald-100'
                                                : 'bg-amber-100'
                                        }`}>
                                            {item.status === 'success' ? (
                                                <CheckCircle size={16} className="text-emerald-600" />
                                            ) : (
                                                <AlertTriangle size={16} className="text-amber-600" />
                                            )}
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                ) : (
                    <div className="px-6 py-12 text-center">
                        <Upload size={40} className="mx-auto text-slate-300 mb-3" />
                        <p className="text-slate-500">{t('importPage.noImportsYet')}</p>
                        <p className="text-sm text-slate-400 mt-1">
                            {t('importPage.startImporting')}
                        </p>
                    </div>
                )}
            </div>

            {}
            <ImportModal
                isOpen={showImportModal}
                onClose={() => {
                    setShowImportModal(false);
                    setSelectedType(null);
                }}
                onImportComplete={handleImportComplete}
                initialType={selectedType}
            />
        </div>
    );
}

export default Import;